import { useMemo } from 'react';
import { ProductFruits } from 'react-product-fruits';
import { useUser } from '@/hooks/user/useUser';
import { useFrontendConfigs } from '@/hooks/useFrontendConfigs';
import { getLocale } from '@/lib/locale';
import { resolveProductFruitsWorkspace } from '@/lib/productFruits/productFruitsUser';

/**
 * Runs Product Fruits against the pre-login or post-login workspace. Keyed by workspace code with
 * `lifeCycle="unmount"`, so crossing login or logout destroys the old workspace and inits the new
 * one. Waits for the first user load so a logged-in visitor is never counted in the pre-login
 * workspace too.
 */
export const ProductFruitsEffect = () => {
    const { configs } = useFrontendConfigs();
    const { user, isLoading } = useUser();
    const isBrowser = typeof window !== 'undefined';

    const workspace = useMemo(
        () => (isBrowser && !isLoading ? resolveProductFruitsWorkspace(configs, user) : null),
        // Depend on the fields that feed the workspace, not `user` object identity, so a refetch
        // doesn't push an identical user update.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isBrowser, isLoading, configs, user?.id, user?.userProfile],
    );

    if (!workspace) {
        return null;
    }

    return (
        <ProductFruits
            key={workspace.workspaceCode}
            workspaceCode={workspace.workspaceCode}
            language={getLocale()}
            user={workspace.user}
            lifeCycle="unmount"
        />
    );
};
